<script setup lang="ts">
    import { computed, nextTick, onMounted, ref, watch } from 'vue';
    import { __ } from '../stores/expressions';
    import { useAskMeStore } from '../stores/ask-me';
    import { IChat } from '../services/api/chat/chat';
    import { useConfirmationDialog } from '../stores/confirmationDialog';
    import { CheckIcon, EllipsisIcon, PencilIcon, TrashIcon } from '@lucide/vue';
    import { useRoute, useRouter } from 'vue-router';
    import EchoPopover from './EchoPopover.vue';
    import IconButton from './IconButton.vue';
    import EchoButton from './EchoButton.vue';

    const ask_me = useAskMeStore();
    const route = useRoute();
    const router = useRouter();

    const props = defineProps<{ chat: IChat }>();

    const edit = ref(false);
    const title = ref<string>('');
    const edit_input = ref<HTMLInputElement | null>(null);
    const currentItem = ref(false);
    const actionsMounted = ref(false);

    const mountActions = () => {
        actionsMounted.value = true;
    };

    onMounted(() => {
        title.value = props.chat.title;
    });

    watch(
        () => route.params.chat,
        (chat) => {
            currentItem.value = props.chat.id === chat;
        },
        { immediate: true }
    );

    watch(
        () => props.chat.title,
        (newTitle) => {
            title.value = newTitle;
        },
        { deep: true }
    );

    watch(
        () => edit.value,
        () => {
            if (edit.value) {
                nextTick(() => {
                    edit_input.value?.focus();
                });
            }
        }
    );

    const save = () => {
        edit.value = false;
        ask_me.updateChatName(props.chat.id, title.value);
    };

    const confirm = useConfirmationDialog();

    const deleteChat = (chatId: string, title: string) => {
        confirm
            .openDialog({
                title: __('ask-me-confirm-delete-chat-title', {
                    title,
                }),
                message: __('ask-me-confirm-delete-chat-description', {
                    title,
                }),
                confirmText: __('ask-me-confirm-delete-chat-button'),
            })
            .then(async (confirmed: boolean) => {
                if (confirmed) {
                    ask_me.deleteChat(chatId);

                    if (route.params.chat === chatId) {
                        router.push('/ask-me');
                    }
                }
            });
    };

    const classes = computed(() => {
        let classes = [];

        if (currentItem.value && !edit.value) {
            classes.push('bg-gray-light/40');
        }

        if (!edit.value) {
            classes.push('hover:bg-gray-light/40');
        }

        return classes.join(' ');
    });
</script>

<template>
    <li
        class="group relative mb-0.5 rounded-sm hover:text-white has-focus:text-white"
        :class="classes"
        @mouseenter="mountActions"
        @focusin="mountActions"
    >
        <RouterLink
            v-if="!edit"
            :to="'/ask-me/' + chat.id"
            class="peer px-2 group-hover:pr-10 group-focus-within:pr-10 py-1.5 border-0 custom-focus block text-sm rounded-sm truncate"
        >
            {{ title }}
        </RouterLink>

        <div v-else class="flex">
            <input
                ref="edit_input"
                v-model="title"
                type="text"
                class="focus:ring-0 custom-focus rounded-sm w-full text-sm border-0 px-2 py-1.5 bg-white text-gray-dark"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
                @keyup.enter="save()"
            />
            <IconButton :tooltip="__('save')" area-size="size-8" @click="save()">
                <CheckIcon />
            </IconButton>
        </div>

        <div
            v-if="actionsMounted && !edit"
            class="absolute top-4 -translate-y-1/2 right-0"
            :class="
                edit
                    ? 'opacity-0 pointer-events-none'
                    : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 peer-focus:opacity-100'
            "
        >
            <EchoPopover type="menu" position="bottom-end">
                <template #button="{ anchor, togglePopover, buttonAttrs }">
                    <IconButton
                        :ref="anchor"
                        :tooltip="__('edit-chat')"
                        area-size="size-8"
                        v-bind="buttonAttrs"
                        @click="togglePopover"
                    >
                        <EllipsisIcon class="size-5" />
                    </IconButton>
                </template>

                <template #popover="{ closePopover }">
                    <EchoButton
                        variant="block"
                        @click="
                            edit = true;
                            closePopover();
                        "
                    >
                        <template #icon-left><PencilIcon /></template>
                        {{ __('edit-chat') }}
                    </EchoButton>
                    <EchoButton
                        variant="block"
                        @click="
                            deleteChat(chat.id, title);
                            closePopover();
                        "
                    >
                        <template #icon-left><TrashIcon /></template>
                        {{ __('delete-chat') }}
                    </EchoButton>
                </template>
            </EchoPopover>
        </div>
    </li>
</template>
